import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { toast } from "sonner";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Settings2, Save, ArrowLeft, Maximize2, Eye } from "lucide-react";
import WidgetGridBuilder from "@/components/WidgetGridBuilder";
import { AdminWidgetLiveBody } from "@/components/widget-bodies/AdminWidgetLiveBody";

/**
 * Dashboard admin custom (Widget Marketplace v2 pour admin).
 * Vue synthèse cross-entreprises avec grille configurable + preview live.
 */
export default function AdminDashboardWidgets() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [enabledMap, setEnabledMap] = useState({});
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: c }, { data: p }, { data: d }] = await Promise.all([
          api.get("/admin/widgets/catalog"),
          api.get("/admin/widgets/preferences"),
          api.get("/admin/widgets/data"),
        ]);
        const cat = c.widgets || [];
        setCatalog(cat);
        setPrefs(p.preferences || {});
        setData(d);
        const em = {};
        cat.forEach((w) => { em[w.key] = !!p.preferences?.[w.key]?.enabled; });
        setEnabledMap(em);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, []);

  const toggle = (key) => setEnabledMap((m) => ({ ...m, [key]: !m[key] }));

  const save = async () => {
    setSaving(true);
    try {
      const merged = { ...prefs };
      Object.keys(enabledMap).forEach((k) => {
        merged[k] = { ...(merged[k] || {}), enabled: enabledMap[k], order: merged[k]?.order ?? 0 };
      });
      await api.post("/admin/widgets/preferences", { preferences: merged });
      toast.success("Préférences enregistrées");
      setEditing(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const resetDefaults = () => {
    const defaults = ["total_entreprises", "mrr", "new_signups_7d", "churn_rate", "recent_cancellations", "inscriptions_pending"];
    const reset = {};
    catalog.forEach((w, i) => {
      reset[w.key] = { enabled: defaults.includes(w.key), order: i };
    });
    setPrefs(reset);
    setEnabledMap(Object.fromEntries(catalog.map((w) => [w.key, defaults.includes(w.key)])));
    toast.info("Défauts restaurés — cliquez Enregistrer pour valider");
  };

  const enabledCount = Object.values(enabledMap).filter(Boolean).length;
  const anyEnabled = enabledCount > 0;

  return (
    <div className="min-h-full" data-testid="admin-dashboard-widgets">
      <PageHeader
        eyebrow="Vue synthèse"
        title="Dashboard admin — Widgets"
        sub={editing ? `Mode édition · ${enabledCount} widget${enabledCount > 1 ? "s" : ""} activé${enabledCount > 1 ? "s" : ""} sur ${catalog.length}` : "Vue cross-entreprises personnalisable"}
        right={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate("/admin")} data-testid="admin-widgets-back" className="font-semibold">
              <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard classique
            </Button>
            {editing ? (
              <Button onClick={save} disabled={saving} data-testid="admin-widgets-save" className="bg-[#002FA7] text-white">
                <Save className="h-4 w-4 mr-2" /> {saving ? "Sauvegarde…" : "Enregistrer"}
              </Button>
            ) : (
              <Button onClick={() => setEditing(true)} data-testid="admin-widgets-edit" className="bg-[#0A0A0A] text-white">
                <Settings2 className="h-4 w-4 mr-2" /> Personnaliser
              </Button>
            )}
          </div>
        }
      />

      <div className="p-6 lg:p-10">
        {editing && (
          <div className="bg-[#F8F9FA] border border-[#DEE2E6] p-3 mb-4 flex items-center justify-between flex-wrap gap-2" data-testid="admin-widgets-toolbar">
            <div className="text-xs text-[#495057] flex items-center gap-2">
              <Maximize2 className="h-3.5 w-3.5" />
              Grille 12 colonnes — glissez pour déplacer, tirez le coin pour redimensionner.
            </div>
            <button onClick={resetDefaults} data-testid="admin-widgets-reset" className="text-xs underline text-[#868E96] hover:text-[#0A0A0A]">
              Restaurer les défauts
            </button>
          </div>
        )}

        {!anyEnabled ? (
          <div className="bg-white border border-dashed border-[#DEE2E6] p-10 text-center" data-testid="admin-widgets-empty">
            <Settings2 className="h-10 w-10 mx-auto text-[#868E96] mb-3" />
            <div className="font-display font-bold text-lg mb-1">Aucun widget activé</div>
            <p className="text-sm text-[#868E96] mb-4">Passez en mode édition pour composer votre dashboard.</p>
            <Button onClick={() => setEditing(true)} className="bg-[#0A0A0A] text-white">Personnaliser</Button>
          </div>
        ) : (
          <div className={editing ? "bg-white border border-[#DEE2E6] p-2" : ""} data-testid="admin-widgets-grid">
            <WidgetGridBuilder
              catalog={catalog}
              enabledMap={enabledMap}
              prefs={prefs}
              onLayoutChange={editing ? setPrefs : undefined}
              onToggle={editing ? toggle : undefined}
              readOnly={!editing}
              testidPrefix="admin"
              renderBody={(widget) => <AdminWidgetLiveBody widget={widget} data={data?.[widget.key]} />}
            />
          </div>
        )}
      </div>
    </div>
  );
}
